import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Space, Avatar, Button, Drawer } from 'antd';
import { FilterState } from '../../types';
import {
  logout,
  fetchClientOverview,
  fetchMismatchedLocations,
  MismatchedEntityType,
  MismatchedLocation,
  MismatchedLocationsResponse,
} from '../../services/api';
import { APP_URL } from '../../env';
import { useUser } from '../../contexts/UserContext';
import { usePermission } from '../../permission/permissionContext';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  BugOutlined,
  GlobalOutlined,
  BarChartOutlined,
  DownOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import DiagnosisOverviewModal from './DiagnosisOverviewModal';
import DiagnosisMismatchDrawer from './DiagnosisMismatchDrawer';
import type { PopupTableRow } from '../MapComponent/utils';

const { Header } = Layout;

const HeaderContainer = styled(Header)`
  position: sticky;
  top: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 8px 16px;

  .ant-menu-overflow-item-rest .ant-menu-submenu-arrow {
    display: none !important;
  }
`;

const MainContentWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;

  @media (min-width: 768px) {
    width: 91.666667%; /* lg:w-11/12 */
  }
`;

const MobileMenuButton = styled(Button)`
  display: block;
  font-size: 1.25rem;
  left: 0;
  z-index: 10;
  color: #374151;

  @media (min-width: 768px) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  width: 100%;
  justify-content: flex-start;
    width: auto;
    display: flex;
`;

const DesktopMenu = styled.div`
  display: none;
  width: 100%;
  margin-left: 2.5rem;
  overflow: hidden;

  @media (min-width: 768px) {
    display: block;
  }
`;

const MenuContainer = styled.div`
  width: 100%;
  
  .ant-menu {
    border: none !important;
    background-color: transparent !important;
  }
  
  .custom-ant-menu .ant-menu-item::after,
  .custom-ant-menu .ant-menu-submenu::after {
    display: none !important;
  }
  
  .custom-ant-menu .ant-menu-item-selected {
    color: #6c5ffc !important;
    background-color: transparent !important;
  }
  
  .custom-ant-menu .ant-menu-item:hover,
  .custom-ant-menu .ant-menu-submenu:hover {
    color: #6c5ffc !important;
  }
  
  .custom-ant-menu .ant-menu-submenu-selected .ant-menu-submenu-title {
    color: #6c5ffc !important;
  }
  
  .custom-ant-menu .ant-menu-submenu-title .ant-menu-submenu-arrow {
    display: inline-block;
    color: currentColor;
  }
  
  .ant-menu-overflow-item {
    flex-shrink: 0;
  }
  
  .ant-menu-submenu-popup {
    z-index: 1050;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const CompanyLogo = styled.img`
  height: 40px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
`;

const DrawerContainer = styled(Drawer)`
  .custom-ant-menu-mobile .ant-menu-item-selected {
    color: #6c5ffc !important;
    background-color: transparent !important;
  }
  
  .custom-ant-menu-mobile .ant-menu-item:hover {
    color: #6c5ffc !important;
  }
  
  .custom-ant-menu-mobile .ant-menu-submenu-selected .ant-menu-submenu-title {
    color: #6c5ffc !important;
  }
  
  .custom-ant-menu-mobile .ant-menu-submenu-title .ant-menu-submenu-arrow {
    display: inline-block !important;
    color: currentColor !important;
  }
    .ant-drawer-body{
      padding: 0;
    }
`;

interface NavbarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  currentView?: 'dashboard' | 'diagnosys';
  onViewChange?: (view: 'dashboard' | 'diagnosys') => void;
  onPrimaryMetricClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({
  filters, 
  onFiltersChange, 
  currentView = 'dashboard',
  onViewChange,
  onPrimaryMetricClick
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [current, setCurrent] = useState<string>(currentView);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [diagnosisModalVisible, setDiagnosisModalVisible] = useState(false);
  const [mismatchDrawerVisible, setMismatchDrawerVisible] = useState(false);
  const [mismatchType, setMismatchType] = useState<MismatchedEntityType | null>(null);
  const [mismatchSearchInput, setMismatchSearchInput] = useState('');
  const [mismatchSearch, setMismatchSearch] = useState('');
  const [mismatchPagination, setMismatchPagination] = useState({ current: 1, pageSize: 10 });
  const { userDetails } = useUser();
  const locationLabel = [filters.city, filters.state, filters.country].filter(Boolean).join(', ') || 'All Locations';

  const mapAllPermission = usePermission('Map_All');
  const primaryMetricsPermission = usePermission('primaryMatrics');
  const diagnosisPermission = usePermission('Diagnosis');

  const triggerTenantCheck = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const broadcastWindow = window as Window & {
      refreshBroadcastMessages?: () => void;
    };

    broadcastWindow.refreshBroadcastMessages?.();
  };

  const { data: clientOverviewData, isFetching: isFetchingClientOverview } = useQuery({
    queryKey: [
      'client-overview',
      filters.country,
      filters.state,
      filters.city,
      filters.startDate,
      filters.endDate,
    ],
    queryFn: () =>
      fetchClientOverview({
        country: filters.country || undefined,
        state: filters.state || undefined,
        city: filters.city || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }),
    enabled: diagnosisModalVisible,
    staleTime: 300000,
  });

  const { data: mismatchedLocationsData, isFetching: isFetchingMismatchedLocations } = useQuery<MismatchedLocationsResponse>({
    queryKey: [
      'mismatched-locations',
      mismatchType,
      filters.country,
      filters.state,
      filters.city,
      filters.startDate,
      filters.endDate,
    ],
    queryFn: () =>
      fetchMismatchedLocations({
        type: mismatchType as MismatchedEntityType,
        country: filters.country || undefined,
        state: filters.state || undefined,
        city: filters.city || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }),
    enabled: mismatchDrawerVisible && !!mismatchType,
  });

  const mismatchLocations: MismatchedLocation[] =
    mismatchDrawerVisible && mismatchType ? mismatchedLocationsData?.locations ?? [] : [];

  const filteredMismatchLocations = useMemo(() => {
    if (!mismatchDrawerVisible || !mismatchType) {
      return [];
    }
    const query = mismatchSearch.trim().toLowerCase();
    if (!query) {
      return mismatchLocations;
    }

    return mismatchLocations.filter((record: MismatchedLocation) => {
      const fields = [
        record.company,
        record.address,
        record.website_found_address,
        record.city,
        record.website_found_city,
        record.state,
        record.website_found_state,
        record.staff_name,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return fields.some((value) => value.includes(query));
    });
  }, [mismatchDrawerVisible, mismatchType, mismatchSearch, mismatchLocations]);

  useEffect(() => {
    if (!mismatchDrawerVisible) {
      return;
    }
    const totalPages = Math.max(
      1,
      Math.ceil((filteredMismatchLocations.length || 0) / mismatchPagination.pageSize),
    );
    if (mismatchPagination.current > totalPages) {
      setMismatchPagination((prev) => ({
        ...prev,
        current: totalPages,
      }));
    }
  }, [filteredMismatchLocations.length, mismatchPagination.pageSize, mismatchDrawerVisible, mismatchPagination.current]);

  const paginatedMismatchLocations = useMemo(() => {
    if (!mismatchDrawerVisible) {
      return [];
    }
    const start = (mismatchPagination.current - 1) * mismatchPagination.pageSize;
    return filteredMismatchLocations.slice(start, start + mismatchPagination.pageSize);
  }, [filteredMismatchLocations, mismatchPagination, mismatchDrawerVisible]);



  useEffect(() => {
    if (currentView === 'diagnosys') {
      setCurrent('diagnosys');
      return;
    }

    if (!diagnosisModalVisible && location.pathname === '/') {
      setCurrent((prev) => (prev === 'diagnosys' ? 'dashboard' : prev));
    }
  }, [currentView, location.pathname, diagnosisModalVisible]);

  // Close mobile drawer when viewport size increases
  useEffect(() => {
    const handleResize = () => {
      // Check if viewport width is larger than the lg breakpoint (768px)
      if (window.innerWidth >= 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleDiagnosisMenuClick = () => {
    triggerTenantCheck();
    setCurrent('diagnosys');
    setDiagnosisModalVisible(true);
  };

  const handleDiagnosisModalClose = () => {
    setDiagnosisModalVisible(false);
    setCurrent('dashboard');
  };

  const handleViewWithoutCoordinates = (type: MismatchedEntityType) => {
    setMismatchType(type);
    setMismatchSearchInput('');
    setMismatchSearch('');
    setMismatchPagination({ current: 1, pageSize: 10 });
    setMismatchDrawerVisible(true);
  };

  const handleMismatchDrawerClose = () => {
    setMismatchDrawerVisible(false);
    setMismatchType(null);
  };

  const handleMismatchSearchSubmit = (value: string) => {
    const normalized = value.trim();
    setMismatchSearch(normalized);
    setMismatchPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleMismatchSearchInputChange = (value: string) => {
    setMismatchSearchInput(value);
    if (!value.trim()) {
      setMismatchSearch('');
      setMismatchPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const handleMismatchPaginationChange = (page: number, pageSize?: number) => {
    setMismatchPagination((prev) => ({
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handleEditMismatchLocation = useCallback(
    (record: MismatchedLocation) => {
      if (!mismatchType) {
        return;
      }

      const popupRow: PopupTableRow = {
        type: mismatchType === 'vendors' ? 'vendor' : mismatchType === 'prospects' ? 'prospect' : 'client',
        name: record.company || '',
        userid: record.userid ? String(record.userid) : undefined,
        location_id: record.id ? String(record.id) : undefined,
        address: record.address || record.website_found_address || '',
        city: record.city || record.website_found_city || '',
        state: record.state || record.website_found_state || '',
        zip: record.zip || '',
        country: record.country?.short_name || record.website_found_country || '',
        staff: record.staff_name || '',
        website_validation_notes: record.website_validation_notes || '',
        website_found_address: record.website_found_address || '',
        website_found_city: record.website_found_city || '',
        website_found_state: record.website_found_state || '',
        website_found_country: record.website_found_country || '',
      };

      window.dispatchEvent(
        new CustomEvent<PopupTableRow>('open-edit-location', {
          detail: popupRow,
        }),
      );
    },
    [mismatchType],
  );



  const menuItems = [
    ...(mapAllPermission !== false ? [{
      key: 'dashboard',
      icon: <GlobalOutlined />,
      label: 'All',
      onClick: () => {
        triggerTenantCheck();
        setCurrent('dashboard');
        onFiltersChange({ ...filters, locationType: ['all'] });
        if (location.pathname === '/diagnosys') {
          navigate('/');
        } else {
          onViewChange?.('dashboard');
        }
      },
    }] : []),
    ...(primaryMetricsPermission !== false ? [{
      key: 'primary-metric',
      icon: <BarChartOutlined />,
      label: 'Primary Metrics',
      onClick: () => {
        onPrimaryMetricClick?.();
      },
    }] : []),
    ...(diagnosisPermission !== false ? [{
      key: 'diagnosys',
      icon: <BugOutlined />,
      label: 'Diagnosis',
      onClick: handleDiagnosisMenuClick,
    }] : []),
  ];

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 600 }}>
            {userDetails ? `${userDetails.firstname} ${userDetails.lastname}` : 'Loading...'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {userDetails?.email || 'Loading...'}
          </div>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => window.location.href = `${APP_URL}/profile`
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log Out',
      onClick: handleLogout
    },
  ];

  const onClick: MenuProps['onClick'] = (e) => {
    // Find the clicked menu item and execute its onClick handler
    const clickedItem = menuItems.find(item => item.key === e.key);
    if (clickedItem && clickedItem.onClick) {
      clickedItem.onClick();
    }
  };


  return (
    <>
      <HeaderContainer>
      {/*<MobileLogo>*/}
      {/*  <img src="/logo.webp" alt="KML Map" style={{ height: 'auto', width: '40px' }} />*/}
      {/*</MobileLogo>*/}
      <MainContentWrapper>
        {/* Mobile hamburger menu - always on left */}
        <MobileMenuButton
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuOpen(true)}
        />
        
        {/* Logo - centered on mobile, normal on desktop */}
        <LogoContainer>
          <a href={APP_URL} style={{ display: 'inline-block', lineHeight: 0 }}>
            <img src="/logo.webp" alt="KML Map" style={{ height: '40px', width: 'auto' }} />
          </a>
        </LogoContainer>

        {/* Desktop Menu - Ant Design Horizontal Menu */}
        <DesktopMenu>
          <MenuContainer>
            <Menu
              onClick={onClick}
              selectedKeys={[current]}
              mode="horizontal"
              items={menuItems}
              triggerSubMenuAction="click"
              overflowedIndicator={
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  More
                  <DownOutlined />
                </span>
              }
              style={{ 
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%'
              }}
              className="custom-ant-menu"
            />
          </MenuContainer>
        </DesktopMenu>
        
        {/* Mobile Menu Drawer */}
        <DrawerContainer
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>Menu</span>
            </div>
          }
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={280}
        >
          <Menu
            onClick={(e) => {
              onClick(e);
              setMobileMenuOpen(false);
            }}
            mode="inline"
            selectedKeys={[current]}
            style={{ borderRight: 0 }}
            items={menuItems}
            className="custom-ant-menu-mobile"
          />
        </DrawerContainer>
      </MainContentWrapper>

      <RightSection>
        {userDetails?.logo_url && (
          <CompanyLogo
            src={userDetails.logo_url}
            alt={`${userDetails?.company_name || 'Company'} logo`}
          />
        )}
        <Dropdown
          menu={{ items: profileMenuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              size="large"
              src={userDetails?.profile_image || undefined}
              icon={!userDetails?.profile_image ? <UserOutlined /> : undefined}
            />
          </Space>
        </Dropdown>
      </RightSection>
      </HeaderContainer>

      <DiagnosisOverviewModal
        open={diagnosisModalVisible}
        onClose={handleDiagnosisModalClose}
        data={clientOverviewData}
        loading={isFetchingClientOverview}
        onViewWithoutCoordinates={handleViewWithoutCoordinates}
        locationLabel={locationLabel}
      />

      <DiagnosisMismatchDrawer
        open={mismatchDrawerVisible}
        onClose={handleMismatchDrawerClose}
        entityType={mismatchType}
        data={mismatchedLocationsData}
        loading={isFetchingMismatchedLocations}
        searchValue={mismatchSearchInput}
        onSearchChange={handleMismatchSearchInputChange}
        onSearch={handleMismatchSearchSubmit}
        pagination={mismatchPagination}
        onPaginationChange={handleMismatchPaginationChange}
        rows={paginatedMismatchLocations}
        filteredCount={filteredMismatchLocations.length}
        onEditLocation={handleEditMismatchLocation}
      />
    </>
  );
};

export default Navbar;
